import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import useOrderEvents from "@/hooks/useOrderEvents";
import KPITiles from "@/components/KPITiles";
import OrdersTable from "@/components/OrdersTable";
import StatsScopeToggle from "@/components/StatsScopeToggle";
import BulkImportButton from "@/components/BulkImportButton";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { PackagePlus, Search, FileDown, Printer } from "lucide-react";
import { toast } from "sonner";
import { downloadOrdersCsv } from "@/lib/exportOrdersCsv";
import { printBulkLabels } from "@/lib/printBulkLabels";
import { makeScopeFilter } from "@/lib/scope";

const SCOPE_LABEL = { today: "Today", week: "This Week", month: "This Month", ytd: "YTD" };

// URL ?status=… shortcuts → local filter dropdown value. Keeps the KPI-tile
// deep-links from the overview page in sync with the dropdown.
const STATUS_QUERY_MAP = {
  accepted: "accepted",
  unassigned: "pending",
  on_route: "assigned",
  assigned: "assigned",
  in_transit: "in_transit",
  delivered: "delivered",
  failed: "failed",
  returned: "returned",
};

export default function PharmacyStaff() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState({});
  // Seed with an optimistic row if we just came back from "New order".
  // This keeps Miles / Price / Pharmacy visible immediately while /orders refetches.
  const [orders, setOrders] = useState(() => (location.state?.newOrder ? [location.state.newOrder] : []));
  const [drivers, setDrivers] = useState([]);
  const [filterStatus, setFilterStatus] = useState(
    STATUS_QUERY_MAP[searchParams.get("status") || ""] || "all",
  );
  const [filterDeliverType, setFilterDeliverType] = useState("all");
  const [filterDriver, setFilterDriver] = useState("all");
  const [search, setSearch] = useState("");
  // Debounce keystrokes before hitting the backend — search matches against
  // the whole Orders collection (older bulk-imported orders live past the
  // 1000-row visibility window) so we need a server round-trip, not just
  // client-side .filter().
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);
  const [scope, setScope] = useState("week");

  // Clear the one-shot newOrder hand-off so a later filter change / refresh
  // doesn't keep re-injecting the same row.
  useEffect(() => {
    if (location.state?.newOrder) {
      window.history.replaceState({}, "");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the dropdown in sync when the URL query changes (e.g. clicking a
  // KPI tile from the overview navigates to /staff/orders?status=accepted).
  useEffect(() => {
    const next = STATUS_QUERY_MAP[searchParams.get("status") || ""] || "all";
    setFilterStatus(next);
  }, [searchParams]);

  const load = () => {
    const params = {};
    if (filterStatus === "accepted") params.accepted = true;
    else if (filterStatus !== "all") params.status = filterStatus;
    if (debouncedSearch) params.search = debouncedSearch;
    Promise.all([
      api.get("/stats/overview", { params: { scope } }),
      api.get("/orders", { params }),
    ]).then(([s, o]) => { setStats(s.data); setOrders(o.data); });
  };

  useEffect(() => {
    api.get("/users", { params: { role: "driver" } }).then((r) => setDrivers(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterStatus, scope, debouncedSearch]);
  useEffect(() => {
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [filterStatus, scope, debouncedSearch]);

  // Real-time push: driver-side status changes land here instantly.
  useOrderEvents((update) => {
    setOrders((prev) => {
      const idx = prev.findIndex((o) => o.id === update.id);
      if (idx < 0) return prev;
      const next = prev.slice();
      next[idx] = { ...prev[idx], ...update };
      return next;
    });
  });

  const filtered = orders.filter(makeScopeFilter(scope)).filter((o) => {
    if (filterDeliverType !== "all" && (o.deliver_type || "") !== filterDeliverType) return false;
    if (filterDriver !== "all") {
      if (filterDriver === "none" ? o.driver_id : o.driver_id !== filterDriver) return false;
    }
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      o.customer_name?.toLowerCase().includes(q) ||
      o.address?.toLowerCase().includes(q) ||
      o.customer_phone?.toLowerCase().includes(q) ||
      o.driver_name?.toLowerCase().includes(q) ||
      o.order_number?.toLowerCase().includes(q)
    );
  });

  const onDelete = async (o) => {
    if (o.status !== "pending") {
      toast.error("Only pending orders can be deleted");
      return;
    }
    if (!confirm(`Delete order for ${o.customer_name}?`)) return;
    try { await api.delete(`/orders/${o.id}`); toast.success("Order deleted"); load(); }
    catch (e) { toast.error("Could not delete order"); }
  };

  const onExportExcel = () => {
    if (!filtered.length) {
      toast.error("No orders to export in the current scope.");
      return;
    }
    const { count } = downloadOrdersCsv(filtered, "pharmacy-orders", scope);
    toast.success(`Exported ${count} order${count === 1 ? "" : "s"} (${SCOPE_LABEL[scope]})`);
  };

  const [printing, setPrinting] = useState(false);
  const [selected, setSelected] = useState(() => new Set());

  // Whenever the filtered list changes, drop selections that are no longer visible.
  useEffect(() => {
    setSelected((prev) => {
      const visibleIds = new Set(filtered.map((o) => o.id));
      const next = new Set();
      prev.forEach((id) => { if (visibleIds.has(id)) next.add(id); });
      return next.size === prev.size ? prev : next;
    });
  }, [filtered]);

  const toggleSelected = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const toggleSelectAll = () => setSelected((prev) => (
    prev.size >= filtered.length ? new Set() : new Set(filtered.map((o) => o.id))
  ));

  const onPrintBulkLabels = async () => {
    // Print selected if any rows checked, otherwise print every filtered row.
    const target = selected.size > 0
      ? filtered.filter((o) => selected.has(o.id))
      : filtered;
    if (!target.length) {
      toast.error("No orders to print.");
      return;
    }
    setPrinting(true);
    try {
      const result = await printBulkLabels(target);
      const what = selected.size > 0 ? "selected" : SCOPE_LABEL[scope];
      toast.success(`Printing ${result.count} label${result.count === 1 ? "" : "s"} (${what})`);
    } catch (e) {
      toast.error(`Could not generate labels: ${e.message || e}`);
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1500px] mx-auto" data-testid="pharmacy-staff-page">
      <header className="mb-4">
        <h1 className="text-3xl lg:text-4xl font-display font-bold tracking-tight">My pharmacy orders</h1>
        <p className="text-muted-foreground mt-1.5">Live status from pickup to doorstep.</p>
      </header>

      {/* Toolbar — scope pills on the LEFT, action buttons on the RIGHT.
          `justify-between` keeps the two groups anchored to opposite ends
          on wide screens; `flex-wrap` lets the action group drop to a
          second row on narrow viewports without squashing. (2026-10-01) */}
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6" data-testid="pharmacy-staff-toolbar">
        <div data-testid="toolbar-scope-group">
          <StatsScopeToggle value={scope} onChange={setScope} showToday />
        </div>
        <div className="flex items-center gap-3 flex-wrap" data-testid="toolbar-actions-group">
          <Button
            variant="outline"
            className="rounded-full"
            onClick={onPrintBulkLabels}
            disabled={printing}
            data-testid="bulk-print-labels-btn"
            title={`Print labels for all ${SCOPE_LABEL[scope]} orders`}
          >
            <Printer className="h-4 w-4 mr-1.5" /> {printing ? "Printing…" : (selected.size > 0 ? `Print ${selected.size} label${selected.size === 1 ? "" : "s"}` : `Print labels`)}
          </Button>
          <Button
            variant="outline"
            className="rounded-full"
            onClick={onExportExcel}
            data-testid="export-excel-btn"
            title={`Export ${SCOPE_LABEL[scope]} orders to Excel-compatible CSV`}
          >
            <FileDown className="h-4 w-4 mr-1.5" /> Export · {SCOPE_LABEL[scope]}
          </Button>
          <Link to="/staff/new">
            <Button className="rounded-full" data-testid="new-order-btn">
              <PackagePlus className="h-4 w-4 mr-1.5" /> New order
            </Button>
          </Link>
          <BulkImportButton onImported={load} />
        </div>
      </div>

      <KPITiles
        items={[
          { label: "Unassigned", value: stats.out_of_route, hint: "Out of route", color: "yellow", onClick: () => setFilterStatus("pending") },
          {
            label: "Accepted",
            value: stats.received_from_pharmacy,
            hint: stats.pending_intake ? `${stats.pending_intake} pending intake` : "from pharmacy",
            color: "lightBlue",
            onClick: () => setFilterStatus("accepted"),
          },
          { label: "On Route", value: stats.routed, hint: "Orders on a route", color: "sky", onClick: () => setFilterStatus("assigned") },
          { label: "Delivered", value: stats.delivered, hint: "Completed today", color: "green", onClick: () => setFilterStatus("delivered") },
          { label: "Failed", value: stats.failed, hint: "Could not deliver", color: "red", onClick: () => setFilterStatus("failed") },
          { label: "Returned", value: stats.returned, hint: "Returned to pharmacy", color: "orange", onClick: () => setFilterStatus("returned") },
          { label: "Total Open", value: stats.open_orders, hint: "All open (not delivered)", onClick: () => setFilterStatus("all") },
          {
            label: "Copays",
            value: stats.copay_collected_count ?? 0,
            hint: stats.copay_outstanding_count
              ? `${stats.copay_outstanding_count} outstanding · $${(stats.copay_outstanding_total ?? 0).toFixed(2)}`
              : `$${(stats.copay_collected_total ?? 0).toFixed(2)} collected`,
            color: "white",
            onClick: () => setFilterStatus("all"),
          },
        ]}
      />

      <Card className="mt-8 p-4 rounded-xl border-border bg-card/80">
        <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search order #, customer, address, driver…"
              className="pl-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="orders-search"
            />
          </div>
          <div className="w-full md:w-40">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="accepted">Accepted</SelectItem>
                <SelectItem value="assigned">Assigned</SelectItem>
                <SelectItem value="in_transit">In transit</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="returned">Returned</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-full md:w-40">
            <Select value={filterDeliverType} onValueChange={setFilterDeliverType}>
              <SelectTrigger data-testid="filter-deliver-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all"><span className="lg:hidden">All types</span><span className="hidden lg:inline">All delivery types</span></SelectItem>
                <SelectItem value="same_day">Same day</SelectItem>
                <SelectItem value="next_day">Next day</SelectItem>
                <SelectItem value="otc">OTC</SelectItem>
                <SelectItem value="pickup">Pick Up</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-full md:w-48">
            <Select value={filterDriver} onValueChange={setFilterDriver}>
              <SelectTrigger data-testid="filter-driver"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All drivers</SelectItem>
                <SelectItem value="none">— No driver —</SelectItem>
                {drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <div className="mt-4">
        <OrdersTable
          orders={filtered}
          showPharmacy={false}
          showDriverAndRoute={true}
          showCopay={true}
          selectable
          selected={selected}
          onToggle={toggleSelected}
          onToggleAll={toggleSelectAll}
          onDelete={onDelete}
          emptyMessage="No orders yet — click 'New order' to create one."
        />
        <div className="text-xs text-muted-foreground mt-2 font-mono-num">{filtered.length} of {orders.length} orders</div>
      </div>
    </div>
  );
}
